import React from 'react';

// Base Shimmer Box Component
export const ShimmerBox = ({ width = "w-full", height = "h-4", className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${width} ${height} ${className}`}></div>
);

// Shimmer Card for Stats
export const ShimmerCard = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="flex items-center justify-between">
      <div>
        <ShimmerBox width="w-24" height="h-4" className="mb-2" />
        <ShimmerBox width="w-16" height="h-8" />
      </div>
      <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
    </div>
  </div>
);

// Shimmer Table Component
export const ShimmerTable = ({ columns = 4, rows = 5 }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200">
      <ShimmerBox width="w-48" height="h-6" />
    </div>
    <div className="p-6">
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="flex space-x-4">
            <ShimmerBox width="w-20" height="h-4" />
            {Array.from({ length: columns }).map((_, colIndex) => (
              <ShimmerBox key={colIndex} width="w-16" height="h-4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Shimmer Transaction Card
export const ShimmerTransactionCard = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
        <div>
          <ShimmerBox width="w-32" height="h-5" className="mb-1" />
          <ShimmerBox width="w-48" height="h-4" />
        </div>
      </div>
      <div className="text-right">
        <ShimmerBox width="w-24" height="h-4" className="mb-1" />
        <ShimmerBox width="w-16" height="h-6" />
      </div>
    </div>
    
    <div className="bg-gray-50 rounded-lg p-4 mb-4">
      <ShimmerBox width="w-24" height="h-5" className="mb-2" />
      <div className="grid grid-cols-2 gap-4">
        <ShimmerBox width="w-20" height="h-4" />
        <ShimmerBox width="w-24" height="h-4" />
        <ShimmerBox width="w-16" height="h-4" />
        <ShimmerBox width="w-20" height="h-4" />
      </div>
    </div>
    
    <div className="mb-4">
      <ShimmerBox width="w-20" height="h-5" className="mb-3" />
      <div className="space-y-2">
        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
          <ShimmerBox width="w-24" height="h-4" />
          <ShimmerBox width="w-16" height="h-4" />
        </div>
      </div>
    </div>
    
    <div className="border-t border-gray-200 pt-4">
      <div className="flex justify-between items-center">
        <ShimmerBox width="w-24" height="h-5" />
        <ShimmerBox width="w-16" height="h-6" />
      </div>
    </div>
  </div>
);

// Shimmer Stats Card
export const ShimmerStatsCard = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center space-x-3">
      <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse"></div>
      <div>
        <ShimmerBox width="w-20" height="h-4" className="mb-1" />
        <ShimmerBox width="w-16" height="h-8" />
      </div>
    </div>
  </div>
);

// Shimmer Chart Component
export const ShimmerChart = ({ height = "h-64" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="mb-4">
      <ShimmerBox width="w-48" height="h-6" className="mb-2" />
      <ShimmerBox width="w-32" height="h-4" />
    </div>
    <div className={`${height} bg-gray-100 rounded-lg animate-pulse flex items-center justify-center`}>
      <div className="text-gray-400 text-sm">Loading chart...</div>
    </div>
  </div>
);

// Shimmer List Component
export const ShimmerList = ({ items = 3, itemComponent: ItemComponent = ShimmerBox }) => (
  <div className="space-y-4">
    {Array.from({ length: items }).map((_, index) => (
      <ItemComponent key={index} />
    ))}
  </div>
);

// Shimmer Grid Component
export const ShimmerGrid = ({ 
  columns = 3, 
  rows = 2, 
  itemComponent: ItemComponent = ShimmerCard,
  className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
}) => (
  <div className={className}>
    {Array.from({ length: columns * rows }).map((_, index) => (
      <ItemComponent key={index} />
    ))}
  </div>
);
